import { getTimeSignature, type TimeSignature } from './meter.ts';

/**
 * The musical truth of a phrase: back-to-back sounds and silences covering
 * every tick of every measure. The generator only ever produces this; notation
 * (with ties, dots and beams) is derived from it by `engravePhrase`, and the
 * taps a student must play come from it — never from the notation — so a tied
 * note's continuation or a rest can never expect a tap.
 */
export interface RhythmEvent {
  /** Ticks from the start of the phrase. */
  start: number;
  duration: number;
  /** A silence (written as one or more rests). */
  rest: boolean;
}

/**
 * A triplet: three equal parts in the time of two. `duration` is the whole
 * group (48 ticks = eighth-note triplet over one quarter; 96 = quarter-note
 * triplet over a half). Events inside sit on its thirds and never cross its edges.
 */
export interface RhythmTuplet {
  start: number;
  duration: number;
}

export interface RhythmPhrase {
  timeSignature: string;
  measures: number;
  events: RhythmEvent[];
  /** Triplet groups, when the phrase has any. */
  tuplets?: RhythmTuplet[];
}

/** The triplet group containing a tick, if any. */
export function tupletAt(phrase: RhythmPhrase, tick: number): RhythmTuplet | undefined {
  return phrase.tuplets?.find((t) => tick >= t.start && tick < t.start + t.duration);
}

export function phraseMeter(phrase: RhythmPhrase): TimeSignature {
  return getTimeSignature(phrase.timeSignature);
}

export function phraseTicks(phrase: RhythmPhrase): number {
  return phraseMeter(phrase).measureTicks * phrase.measures;
}

/** Tick positions where the student must tap: the start of every sound. */
export function expectedTapTicks(phrase: RhythmPhrase): number[] {
  return phrase.events.filter((e) => !e.rest).map((e) => e.start);
}

/** Structural check used by tests and by the grader's input validation. */
export function isWellFormedPhrase(phrase: RhythmPhrase): boolean {
  let cursor = 0;
  for (const e of phrase.events) {
    if (e.start !== cursor || e.duration <= 0 || !Number.isInteger(e.duration)) return false;
    cursor += e.duration;
  }
  if (cursor !== phraseTicks(phrase)) return false;
  // Triplets: on their thirds, inside the phrase, not overlapping, with no event crossing an edge.
  const tuplets = [...(phrase.tuplets ?? [])].sort((a, b) => a.start - b.start);
  for (const [i, t] of tuplets.entries()) {
    if (t.duration % 3 !== 0 || t.start < 0 || t.start + t.duration > cursor) return false;
    if (i > 0 && t.start < tuplets[i - 1]!.start + tuplets[i - 1]!.duration) return false;
    const unit = t.duration / 3;
    for (const e of phrase.events) {
      const end = e.start + e.duration;
      const overlaps = e.start < t.start + t.duration && end > t.start;
      if (!overlaps) continue;
      if (e.start < t.start || end > t.start + t.duration) return false;
      if ((e.start - t.start) % unit !== 0 || e.duration % unit !== 0) return false;
    }
  }
  return true;
}
